'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

function useIntersectionObserver(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {setIsVisible(true);observer.unobserve(el);}
      },
      { threshold }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Our Wines', href: '/wines' },
  { label: 'Winemaking', href: '/winemaking' },
  { label: 'Team', href: '/team' },
  { label: 'Contact', href: '/contact' }];


  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center transition-all duration-500"
        style={{ background: 'rgba(10,7,5,0.97)', backdropFilter: 'blur(20px)', opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none' }}>
        <button className="absolute top-6 right-6 p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
        </button>
        <nav className="flex flex-col gap-8 text-center">
          {navLinks?.map((link) =>
          <Link key={link?.href + link?.label} href={link?.href}
          className="font-display text-3xl font-medium italic text-[#F5EDD6] hover:text-[#C9A84C] transition-colors duration-300"
          onClick={() => setMenuOpen(false)}>{link?.label}</Link>
          )}
        </nav>
        <div className="mt-10 text-[#9A8A72] text-xs font-sans uppercase tracking-[0.3em]">Est. 1850 · Pauillac, France</div>
      </div>

      <div className="gradient-blur"><div /><div /><div /><div /><div /></div>

      <nav className="fixed z-50 top-0 inset-x-0 px-6 py-3 flex items-center justify-between transition-all duration-500"
      style={{ background: scrolled ? 'rgba(10,7,5,0.85)' : 'transparent', backdropFilter: scrolled ? 'blur(12px)' : 'none', borderBottom: scrolled ? '1px solid rgba(46,26,16,0.6)' : 'none' }}>
        <Link href="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
              <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
              <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
            </svg>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-base font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
            <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
          </div>
        </Link>
        <div className="hidden lg:flex items-center gap-7">
          {navLinks?.map((link) =>
          <Link key={link?.href + link?.label} href={link?.href}
          className="text-sm font-medium text-[#9A8A72] hover:text-[#F5EDD6] transition-colors duration-300 relative group">
              {link?.label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link href="/contact" className="hidden md:inline-flex items-center gap-2 px-5 py-2 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
            Visit Estate
          </Link>
          <button className="lg:hidden p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" /></svg>
          </button>
        </div>
      </nav>
    </>);

}

function Footer() {
  return (
    <footer className="border-t border-[#2E1A10] bg-[#0A0705] pt-16 pb-8">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12 mb-12">
          <div className="max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
                <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
                <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
              </svg>
              <div className="flex flex-col leading-none">
                <span className="font-display text-lg font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
                <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
              </div>
            </div>
            <p className="text-[#9A8A72] text-sm leading-relaxed italic font-display">A historic Bordeaux château crafting wines of extraordinary depth and elegance since 1850.</p>
            <p className="text-[#9A8A72] text-xs mt-4 font-sans">Route des Châteaux, Saint Lambert<br />33250 Pauillac, Gironde, France</p>
          </div>
          <div className="flex flex-wrap gap-x-16 gap-y-8">
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Estate</span>
              <Link href="/" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Home</Link>
              <Link href="/about" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Heritage</Link>
              <Link href="/winemaking" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Winemaking</Link>
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Discover</span>
              <Link href="/wines" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Wines</Link>
              <Link href="/team" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Team & Investors</Link>
              <Link href="/contact" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Visit Us</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-[#2E1A10] pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-xs text-[#9A8A72] font-sans">© 2025 圣兰博之围 · Château Baron, Pauillac AOC · All rights reserved</span>
          <div className="flex gap-6">
            <Link href="/contact" className="text-xs text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Privacy</Link>
            <Link href="/contact" className="text-xs text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>);

}

const STATS = [
{ value: '1850', label: 'Year Founded' },
{ value: '45 ha', label: 'Hectares of Vines' },
{ value: '175+', label: 'Years of Heritage' },
{ value: '150K', label: 'Bottles Annually' },
{ value: '18 mo', label: 'Months in Oak' }];


const WINES_PREVIEW = [
{
  number: '01',
  name: '圣兰博之围 Grand Vin',
  vintage: '2019',
  type: 'Pauillac AOC — Grand Vin',
  score: '98 pts',
  notes: 'Deep garnet. Blackcurrant, cedar, graphite, tobacco leaf. Monumental structure, silky tannins, 40+ year aging potential.',
  image: 'https://images.unsplash.com/photo-1583541318663-e2bbb41cac0c?w=600&q=80',
  alt: 'Dark red Bordeaux wine bottle against stone cellar background'
},
{
  number: '02',
  name: 'Le Second de Baron',
  vintage: '2020',
  type: 'Pauillac AOC — Second Wine',
  score: '93 pts',
  notes: 'Approachable elegance. Red and dark fruits, spice, gentle cedar. Ready in 5–8 years.',
  image: 'https://images.unsplash.com/photo-1607720336444-7990bedc2bf9?w=600&q=80',
  alt: 'Elegant wine bottle with gold label on dark background'
},
{
  number: '03',
  name: 'Blanc de Baron',
  vintage: '2022',
  type: 'Bordeaux Blanc AOC',
  score: '94 pts',
  notes: 'Citrus blossom, white peach, wet limestone. Vibrant acidity, long mineral finish.',
  image: 'https://images.unsplash.com/photo-1452443191217-08a4f1f238e6?w=600&q=80',
  alt: 'White wine bottle in vineyard at golden hour'
}];


const TESTIMONIALS = [
{
  quote: "Baron Castle's 2015 Grand Vin is a benchmark for Pauillac — extraordinary concentration, flawless structure, and a finish that lingers for minutes. A wine for the ages.",
  author: 'James Whitfield',
  role: 'Master of Wine, London',
  wine: '2015 Grand Vin',
  score: '97/100'
},
{
  quote: "The terroir of Saint-Lambert shines through every glass. Baron Castle represents the finest tradition of Bordeaux — unyielding quality, authentic expression, and remarkable longevity.",
  author: 'Isabelle Moreau',
  role: 'Sommelier, Restaurant Alain Ducasse, Paris',
  wine: '2018 Grand Vin',
  score: '96/100'
},
{
  quote: "Few estates in Pauillac have maintained such consistency over decades. The 2016 vintage is a monument — cassis, graphite, tobacco, and that unmistakable Médoc gravitas.",
  author: 'Robert Chen',
  role: 'Fine Wine Critic, Wine Spectator',
  wine: '2016 Grand Vin',
  score: '98/100'
}];


function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
  { src: 'https://img.rocket.new/generatedImages/rocket_gen_img_144ad3fc0-1772055259762.png', alt: 'Vineyard at golden hour, rows of vines stretching to the horizon in Pauillac' },
  { src: 'https://images.unsplash.com/photo-1599387227823-212b344c81af?w=1600&q=80', alt: 'Château stone architecture facade with ivy-covered walls at Baron Castle' },
  { src: 'https://images.unsplash.com/photo-1733664094821-f215b68cfada?w=1600&q=80', alt: 'Dark wine cellar with rows of French oak barrels aging Grand Vin' },
  { src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1412d9055-1779613294114.png', alt: 'Deep red Bordeaux wine being poured into crystal glass' }];


  useEffect(() => {
    const timer = setInterval(() => setCurrentSlide((prev) => (prev + 1) % slides?.length), 5000);
    return () => clearInterval(timer);
  }, [slides?.length]);

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden">
      {/* Background slides */}
      {slides?.map((slide, i) =>
      <div key={i} className="absolute inset-0 transition-opacity duration-1000"
      style={{ opacity: i === currentSlide ? 1 : 0 }}>
          <img src={slide?.src} alt={slide?.alt} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(10,7,5,0.95) 0%, rgba(10,7,5,0.5) 50%, rgba(10,7,5,0.2) 100%)' }} />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-16 pb-24 pt-40 w-full">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#C9A84C]" />
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">Pauillac · Grand Cru · Est. 1850</span>
        </div>
        <h1 className="font-display font-medium text-[#F5EDD6] leading-none mb-6 select-none"
        style={{ fontSize: 'clamp(4rem, 14vw, 14rem)' }}>
          BARON CASTLE
        </h1>
        <p className="font-display italic text-lg md:text-xl text-[#F5EDD6]/70 max-w-2xl leading-relaxed mb-10">
          Where the terroir of Pauillac speaks through every vintage. A century and a half of uncompromising craft.
        </p>
        <div className="flex flex-wrap gap-4">
          <Link href="/wines" className="inline-flex items-center gap-3 px-8 py-3 bg-[#C9A84C] text-[#0A0705] text-xs font-semibold uppercase tracking-widest hover:bg-[#F5EDD6] transition-all duration-300">
            Discover Our Wines
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </Link>
          <Link href="/about" className="inline-flex items-center gap-3 px-8 py-3 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
            Our Heritage
          </Link>
        </div>

        {/* Slide indicators */}
        <div className="flex gap-2 mt-12">
          {slides?.map((_, i) =>
          <button key={i} onClick={() => setCurrentSlide(i)}
          className="h-px transition-all duration-300"
          style={{ width: i === currentSlide ? '32px' : '16px', background: i === currentSlide ? '#C9A84C' : 'rgba(201,168,76,0.3)' }}
          aria-label={`Go to slide ${i + 1}`} />
          )}
        </div>
      </div>
    </section>);

}

function StatsBar() {
  const { ref, isVisible } = useIntersectionObserver(0.2);
  return (
    <section className="border-y border-[#2E1A10] bg-[#0A0705]">
      <div ref={ref} className="max-w-[1400px] mx-auto px-6 lg:px-16 py-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-0"
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        {STATS?.map((stat, i) =>
        <div key={stat?.label} className="flex flex-col items-center justify-center py-4 px-4 border-r border-[#2E1A10] last:border-r-0"
        style={{ transitionDelay: `${i * 0.08}s` }}>
            <span className="font-display text-2xl md:text-3xl font-medium text-[#C9A84C] mb-1">{stat?.value}</span>
            <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-[#9A8A72] text-center">{stat?.label}</span>
          </div>
        )}
      </div>
    </section>);

}

function HeritageSection() {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  return (
    <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
      <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1549376557-174eb285b354?w=900&q=80"
            alt="Château facade with ivy-covered stone walls and grand entrance gates at Baron Castle"
            className="w-full object-cover"
            style={{ aspectRatio: '4/5', filter: 'brightness(0.85)' }} />
          
          <div className="absolute bottom-6 left-6 right-6">
            <div className="border border-[#C9A84C]/30 bg-[#0A0705]/80 backdrop-blur-sm px-6 py-4">
              <span className="font-display italic text-[#F5EDD6]/80 text-sm">"The vine does not rush. Neither do we."</span>
              <div className="flex items-center gap-2 mt-2">
                <span className="w-4 h-px bg-[#C9A84C]" />
                <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-[#C9A84C]">Henri-Auguste Baron, 1858</span>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">01 — Our Heritage</span>
          </div>
          <h2 className="font-display font-medium text-[#F5EDD6] leading-tight mb-8"
          style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
            A Legacy Rooted in Pauillac Terroir
          </h2>
          <p className="text-[#9A8A72] leading-relaxed mb-6">
            In 1850, the Baron family planted their first vines on the gravel ridges of Saint-Lambert, drawn by the same ancient soils that would nurture three of the world's First Growths. What began as a single estate has grown into one of Pauillac's most storied châteaux.
          </p>
          <p className="text-[#9A8A72] leading-relaxed mb-10">
            Today, those same principles — respect for terroir, patience in the cellar, and uncompromising standards — guide every harvest. Our 45 hectares of vines, predominantly Cabernet Sauvignon, produce wines that carry the unmistakable signature of Pauillac: power, structure, and extraordinary longevity.
          </p>
          <Link href="/about" className="inline-flex items-center gap-3 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:text-[#F5EDD6] transition-colors group">
            Discover Our Full Story
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="group-hover:translate-x-1 transition-transform"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </Link>
        </div>
      </div>
    </section>);

}

function WinesSection() {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  return (
    <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between mb-14">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C] block mb-4">02 — The Wines</span>
          <h2 className="font-display font-medium text-[#F5EDD6] leading-none"
          style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>
            Expressions of Pauillac
          </h2>
        </div>
        <Link href="/wines" className="hidden md:inline-flex items-center gap-2 text-xs font-sans uppercase tracking-widest text-[#9A8A72] hover:text-[#C9A84C] transition-colors">
          View Full Collection
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </Link>
      </div>
      <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#2E1A10]"
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
        {WINES_PREVIEW?.map((wine, i) =>
        <div key={wine?.number} className="border-r border-[#2E1A10] last:border-r-0 flex flex-col"
        style={{ transitionDelay: `${i * 0.12}s` }}>
            <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
              <img src={wine?.image} alt={wine?.alt} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(10,7,5,0.8) 0%, transparent 60%)' }} />
              <div className="absolute top-4 left-4">
                <span className="font-sans text-xs tracking-[0.2em] text-[#C9A84C]">{wine?.number}</span>
              </div>
              <div className="absolute bottom-4 right-4">
                <span className="font-display text-lg font-medium text-[#C9A84C]">{wine?.score}</span>
              </div>
            </div>
            <div className="p-6 flex flex-col flex-1">
              <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-[#9A8A72] mb-2">{wine?.type} · {wine?.vintage}</span>
              <h3 className="font-display font-medium text-[#F5EDD6] text-xl mb-3">{wine?.name}</h3>
              <p className="text-[#9A8A72] text-sm leading-relaxed flex-1">{wine?.notes}</p>
              <Link href="/wines" className="mt-5 inline-flex items-center gap-2 text-[10px] font-sans uppercase tracking-widest text-[#C9A84C] hover:text-[#F5EDD6] transition-colors">
                Learn More
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </Link>
            </div>
          </div>
        )}
      </div>
      <div className="mt-8 md:hidden text-center">
        <Link href="/wines" className="inline-flex items-center gap-2 text-xs font-sans uppercase tracking-widest text-[#C9A84C] hover:text-[#F5EDD6] transition-colors">
          View Full Collection
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </Link>
      </div>
    </section>);

}

function WinemakingSection() {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  const pillars = [
  { num: '01', title: 'Terroir First', body: 'Deep Günzian gravel soils, the Gironde microclimate, and 175 years of vine wisdom — the land speaks, we listen.' },
  { num: '02', title: 'Hand Harvested', body: 'Every grape selected by hand at peak phenolic ripeness. Sorting tables ensure only the finest fruit enters our cellars.' },
  { num: '03', title: '18–24 Months in Oak', body: 'French oak barriques, 30% new, impart structure and complexity without overwhelming the fruit\'s natural expression.' }];

  return (
    <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
      <div className="h-px bg-[#2E1A10] mb-24" />
      <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start"
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C] block mb-6">03 — Winemaking</span>
          <h2 className="font-display font-medium text-[#F5EDD6] leading-tight mb-8"
          style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
            The Art of Patience
          </h2>
          <blockquote className="font-display italic text-[#F5EDD6]/60 text-lg leading-relaxed mb-10 border-l-2 border-[#C9A84C]/30 pl-6">
            "Great wine is not made — it is cultivated, coaxed, and ultimately revealed through the passage of time."
            <footer className="mt-3 text-[10px] font-sans uppercase tracking-[0.2em] text-[#C9A84C] not-italic">— Maître de Chai, 圣兰博之围</footer>
          </blockquote>
          <div className="flex flex-col gap-6">
            {pillars?.map((p) =>
            <div key={p?.num} className="flex gap-5">
                <span className="font-sans text-xs tracking-[0.2em] text-[#C9A84C] shrink-0 mt-0.5">{p?.num}</span>
                <div>
                  <h4 className="font-sans text-xs uppercase tracking-[0.2em] text-[#F5EDD6] mb-2">{p?.title}</h4>
                  <p className="text-[#9A8A72] text-sm leading-relaxed">{p?.body}</p>
                </div>
              </div>
            )}
          </div>
          <Link href="/winemaking" className="mt-10 inline-flex items-center gap-3 px-8 py-3 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
            Explore Our Process
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <img
            src="https://images.unsplash.com/photo-1452443191217-08a4f1f238e6?w=900&q=80"
            alt="Vineyard rows at sunset with golden light illuminating the Pauillac vines"
            className="w-full object-cover"
            style={{ aspectRatio: '16/9' }} />
          
          <img
            src="https://images.unsplash.com/photo-1657053488881-86657ee81726?w=900&q=80"
            alt="French oak barrels aging Grand Vin in the stone cellar of Baron Castle"
            className="w-full object-cover"
            style={{ aspectRatio: '16/9' }} />
          
        </div>
      </div>
    </section>);

}

function TestimonialsSection() {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  return (
    <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
      <div className="h-px bg-[#2E1A10] mb-24" />
      <div className="flex items-center gap-3 mb-14">
        <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">04 — Critical Acclaim</span>
        <span className="w-8 h-px bg-[#2E1A10]" />
        <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#9A8A72]">Voices of the Connoisseur</span>
      </div>
      <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-0"
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
        {TESTIMONIALS?.map((t, i) =>
        <div key={i} className="border border-[#2E1A10] p-8 flex flex-col justify-between -ml-px first:ml-0"
        style={{ transitionDelay: `${i * 0.1}s` }}>
            <div>
              <div className="flex gap-1 mb-6">
                {[...Array(5)]?.map((_, s) =>
              <svg key={s} width="12" height="12" viewBox="0 0 24 24" fill="#C9A84C"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
              )}
              </div>
              <blockquote className="font-display italic text-[#F5EDD6]/80 text-base leading-relaxed mb-6">
                "{t?.quote}"
              </blockquote>
            </div>
            <div>
              <div className="h-px bg-[#2E1A10] mb-5" />
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="font-sans text-xs font-semibold text-[#F5EDD6] block">{t?.author}</span>
                  <span className="font-sans text-[10px] text-[#9A8A72]">{t?.role}</span>
                </div>
                <div className="text-right shrink-0">
                  <span className="font-display text-xl font-medium text-[#C9A84C] block">{t?.score}</span>
                  <span className="font-sans text-[10px] text-[#9A8A72]">{t?.wine}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>);

}

export default function HomePage() {
  return (
    <main className="bg-[#0A0705] text-[#F5EDD6] overflow-x-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navigation />
      <HeroSection />
      <StatsBar />
      <HeritageSection />
      <WinesSection />
      <WinemakingSection />
      <TestimonialsSection />
      <Footer />
    </main>);

}